import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
    useMediaQuery,
    useScrollTrigger,
    Fade,
    Avatar,
    Chip,
    CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    CheckCircle,
    Email,
    KeyboardArrowDown,
    KeyboardArrowRight,
    School,
    Star,
    Cake,
    WorkHistory
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Company logos
const companies = [
    { name: "Google", logo: "https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_272x92dp.png" },
    { name: "Microsoft", logo: "https://img-prod-cms-rt-microsoft-com.akamaized.net/cms/api/am/imageFileData/RE1Mu3b?ver=5c31" },
    { name: "Amazon", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Amazon_logo.svg/2560px-Amazon_logo.svg.png" },
    { name: "Adobe", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Adobe_Systems_logo_and_wordmark.svg/2560px-Adobe_Systems_logo_and_wordmark.svg.png" },
    { name: "ZS Associates", logo: "https://upload.wikimedia.org/wikipedia/commons/5/59/ZS_Associates.svg" },
    { name: "Ion Trading", logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSbQW-caA2pQxH5-J9wpym-bdpnKQzqj9GrBA&s" },
    { name: "Veersa", logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTJ1c87RxhJtyYKLwZklmCpIr10I9cmHQgSLg&s" },
    { name: "Infosys", logo: "https://www.infosys.com/content/dam/infosys-web/en/global-resource/media-resources/infosys-logo.jpg" },
    { name: "Polestar", logo: "https://www.greatplacetowork.in/great/api/assets/uploads/12719/logo/logo.png" },
    { name: "Newgen", logo: "https://bfsi.eletsonline.com/wp-content/uploads/2017/12/newgen_logo-1.png" },
    { name: "Cognizant", logo: "https://1000logos.net/wp-content/uploads/2021/09/Cognizant-Logo.jpg" },
    { name: "Accenture", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/cd/Accenture.svg/2560px-Accenture.svg.png" },
    { name: "TCS", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Tata_Consultancy_Services_old_logo.svg/2560px-Tata_Consultancy_Services_old_logo.svg.png" }
];

// Years for placement records
const placementYears = [
    { year: "2024", link: "https://www.msit.in/media/uploads/2025/06/02/placement-status-batch-2024.pdf" },
    { year: "2023", link: "https://www.msit.in/media/2024/04/09/batch-2023-placement-status-final.pdf" },
    { year: "2022", link: "https://www.msit.in/media/2024/04/09/batch-2022-placement-status-final.pdf" },
    { year: "2021", link: "https://www.msit.in/media/2021/08/26/2021-tpo.pdf" },
    { year: "2020", link: "https://www.msit.in/media/2021/09/08/placement.pdf" },
    { year: "2019", link: "https://www.msit.in/media/2019/05/14/nareshfinal2019.pdf" },
    { year: "2018", link: "https://www.msit.in/media/2018/09/13/placement-2018.pdf" },
    { year: "2017", link: "https://www.msit.in/media/2017/08/11/placements2017.pdf" },
    { year: "2016", link: "https://www.msit.in/media/2017/03/15/updated_pplacements_2016_sheet_xls_-_sheet1.pdf" },
    { year: "2015", link: "https://www.msit.in/media/2018/08/24/placment-2015.pdf" },
];

const facultyCoordinators = [
    { name: "Dr. Parul Chaudhary", class: "CSE-1" },
    { name: "Dr. Shaily Malik", class: "CSE-2" },
    { name: "Dr. Pooja Kherwa", class: "CSE-3" },
    { name: "Ms. Gunjan", class: "CSE-E" },
    { name: "Dr. Sonika Malik", class: "IT-1" },
    { name: "Ms. Meena Siwach", class: "IT-2" },
    { name: "Dr. Minakshi Tomer", class: "IT-E" },
    { name: "Dr. Sakshi Rajput", class: "ECE-1" },
    { name: "Ms. Himani", class: "ECE-2" },
    { name: "Ms. Sumanlata", class: "ECE-E" },
    { name: "Dr. Rakhi Kamra", class: "EEE" },
    { name: "Mr. Sachit Rathee", class: "EEE" }
];

// Scroll to top component
function ScrollTop(props) {
    const { children } = props;
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 100,
    });

    const handleClick = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    return (
        <Fade in={trigger}>
            <Box
                onClick={handleClick}
                role="presentation"
                sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 2 }}
            >
                {children}
            </Box>
        </Fade>
    );
}

const Landing = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [activeSection, setActiveSection] = useState(null);
    const [userRole, setRole] = useState("user");
    const [authLoading, setauthLoading] = useState(true);
    const navigate = useNavigate();

    // Track scroll for animations
    useEffect(() => {
        const handleScroll = () => {
            const sections = document.querySelectorAll('.section');

            sections.forEach(section => {
                const sectionTop = section.getBoundingClientRect().top;
                if (sectionTop < window.innerHeight * 0.75 && sectionTop > 0) {
                    setActiveSection(section.id);
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const token = localStorage.getItem('token');
                const userId = localStorage.getItem('userId');


                if (!token || !userId) {
                    setIsAuthenticated(false);
                    return;
                }

                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}api/users/${userId}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                if (response.status === 200) {
                    const userRole = response.data.isAdmin ? 'admin' : 'user';
                    setRole(userRole);

                    setIsAuthenticated(true);
                }
            } catch (error) {
                console.error('Authentication check failed:', error);
            } finally {
                setauthLoading(false);
            }
        };

        checkAuth();
    }, []);


    return (
        <>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1976d2 0%, #1976d2b8 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    pt: 6,
                    pb: isMobile ? 10 : 15
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={5} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{ position: 'relative', zIndex: 1 }}>
                                <Typography
                                    variant="h2"
                                    component="h1"
                                    fontWeight="bold"
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '2.2rem', md: '3.5rem' },
                                        textShadow: '0px 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                >
                                    MSIT Placement Cell
                                </Typography>
                                <Typography
                                    variant="h5"
                                    gutterBottom
                                    sx={{
                                        mb: 4,
                                        fontWeight: 300,
                                        fontSize: { xs: '1.2rem', md: '1.5rem' }
                                    }}
                                >
                                    Shaping Careers, Building Futures
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {authLoading ? <CircularProgress size={20} color="inherit" /> : <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={() => {
                                            window.location.replace(isAuthenticated ? (userRole == "user" ? "/home" : "/admin") : "/sign-in");
                                        }}
                                        sx={{
                                            py: 1.2,
                                            px: 3,
                                            borderRadius: 2,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontSize: '1rem',
                                            borderColor: 'white',
                                            color: 'white',
                                            '&:hover': {
                                                borderColor: 'white',
                                                bgcolor: 'rgba(255,255,255,0.1)'
                                            }
                                        }}
                                    >
                                        {isAuthenticated ? "Go to Dashboard" : "Login"}
                                    </Button>}
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={() => {
                                            window.open("https://msit.in/", "_blank")
                                        }}
                                        sx={{
                                            py: 1.2,
                                            px: 3,
                                            borderRadius: 2,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontSize: '1rem',
                                            borderColor: 'white',
                                            color: 'white',
                                            '&:hover': {
                                                borderColor: 'white',
                                                bgcolor: 'rgba(255,255,255,0.1)'
                                            }
                                        }}
                                    >
                                        About MSIT
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    mt: { xs: 4, md: 0 }
                                }}
                            >
                                <Box
                                    component="img"
                                    src="https://msit.in/static/img/msit.png"
                                    alt="MSIT Logo"
                                    sx={{
                                        width: { xs: '80%', md: '80%' },
                                        maxWidth: 400,
                                        filter: 'drop-shadow(0px 10px 15px rgba(0,0,0,0.2))',
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>

                {/* Decorative elements */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -80,
                        left: 0,
                        right: 0,
                        height: '120px',
                        bgcolor: '#fff',
                        transform: 'skewY(-3deg)',
                        zIndex: 0
                    }}
                />
            </Box>

            {/* Stats Highlights */}
            {/* <Box sx={{ bgcolor: 'white', pt: 5, zIndex: 1, position: 'relative' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} justifyContent="center">
                        {[
                            { value: '150+', label: 'Companies' },
                            { value: '95%', label: 'Placement Rate' },
                            { value: '30 LPA', label: 'Highest Package' },
                            { value: '10+ LPA', label: 'Average Package' }
                        ].map((stat, index) => (
                            <Grid item xs={6} md={3} key={index}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 3,
                                        textAlign: 'center',
                                        height: '100%',
                                        borderRadius: 2,
                                        transition: 'transform 0.3s',
                                        '&:hover': { transform: 'translateY(-5px)' },
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Typography variant="h3" fontWeight="bold" color="primary">
                                        {stat.value}
                                    </Typography>
                                    <Typography variant="subtitle1" color="text.secondary">
                                        {stat.label}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container> 
            </Box>*/}

            {/* About Department Section */}
            <Box id="about" className="section" sx={{ py: 10 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={5} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                                ABOUT THE DEPARTMENT
                            </Typography>
                            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
                                Training & Placement Department
                            </Typography>
                            <Typography variant="body1" paragraph>
                                There is an active training & placement department in the campus which is putting in all out efforts
                                for organizing several training & placement activities throughout the academic year for Maharaja
                                Surajmal Group of Institutions, MSGI (MSIT/MSI).
                            </Typography>
                            <Typography variant="body1" paragraph>
                                The department is headed by Prof. H.S. Rawat, Director – T&P, SMES. The placement department has given
                                wonderful opportunities to our students in various companies of repute with handsome packages. There is
                                concerted focus to ensure that students work towards academic excellence to get the best results, keeping
                                in mind the industry bottom line of 60% & above in their respective professional streams.
                            </Typography>
                            <List>
                                {['Regular industry training sessions', 'Professional skill development workshops', 'Mock interviews', 'Industrial visits', 'Project guidance'].map((item, index) => (
                                    <ListItem key={index} sx={{ p: 0, mb: 1 }}>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <CheckCircle color="primary" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={item} />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                component="img"
                                src="https://ipubuzz.com/wp-content/uploads/2020/08/3EE47F7537F01FC4-1-1024x682.jpeg"
                                alt="Students in a placement process"
                                sx={{
                                    width: '100%',
                                    borderRadius: 3,
                                    boxShadow: '0 15px 25px rgba(0,0,0,0.15)',
                                    transform: 'perspective(1000px) rotateY(-5deg)',
                                    transition: 'transform 0.5s',
                                    '&:hover': {
                                        transform: 'perspective(1000px) rotateY(0deg)'
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 6 }}>
                        <Typography variant="body1" paragraph>
                            To give them a feel of industrial environment, students undergo industrial training as part of their curriculum.
                            They also undertake minor industry related project in pre-final semester and a major project in the final semester.
                            Special sessions to hone up technical skills & much needed communication skills are conducted on a regular basis.
                        </Typography>

                        <Typography variant="body1" paragraph>
                            MSIT has a very good track record of placements. Almost all interested students are well placed through the
                            Campus Placements in well known companies like: Google, Microsoft IDC, ADOBE, Infosys, Accenture, TCS, MuSigma,
                            Amazon, Hashed-in, Samsung(R&D), SAP labs, Josh Technologies, ZIA Semiconductors, Aricent, Yamaha Motors,
                            Traveloka, HSBC(ACEs), Postman Inc., Paytm, Airtell Payment, Ion Trading, Social Cops, ZS Associates, Cvent & many more.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* Top Recruiters Section */}
            <Box
                sx={{
                    py: 10,
                    bgcolor: '#f8f9fa',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                            INDUSTRY PARTNERSHIPS
                        </Typography>
                        <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
                            Major Recruiters
                        </Typography>
                        <Typography variant="body1" sx={{ maxWidth: 700, mx: 'auto' }}>
                            Our students are regularly placed at top companies across various industries, with competitive packages and roles.
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 5 }}>
                        <Grid container spacing={3} justifyContent="center">
                            {companies.map((company, index) => (
                                <Grid item xs={6} sm={4} md={3} key={index}>
                                    <Paper
                                        elevation={2}
                                        sx={{
                                            p: 3,
                                            height: 100,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.3s',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                                            },
                                            borderRadius: 2
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={company.logo}
                                            alt={company.name}
                                            sx={{
                                                maxWidth: '100%',
                                                maxHeight: '80px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Container>
            </Box>

            {/* Placement Records Section */}
            <Box id="placement-records" className="section" sx={{ py: 10 }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                            OUR TRACK RECORD
                        </Typography>
                        <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
                            Placement Statistics
                        </Typography>
                        <Typography variant="body1" sx={{ maxWidth: 700, mx: 'auto' }}>
                            MSIT has consistently maintained an excellent placement record over the years.
                        </Typography>
                    </Box>

                    <Grid container spacing={3} justifyContent="center">
                        {placementYears.map((item, index) => (
                            <Grid item xs={6} sm={4} md={2.4} key={index}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => window.open(item.link, "_blank")}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        fontWeight: 'bold',
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                            bgcolor: 'primary.main',
                                            color: 'white'
                                        }
                                    }}
                                >
                                    Placements {item.year}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>

                    <Box sx={{ mt: 8, textAlign: 'center' }}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 4,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={8} sx={{ textAlign: 'left' }}>
                                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                                        Ready to Kickstart Your Career?
                                    </Typography>
                                    <Typography variant="body1" paragraph>
                                        Join MSIT's placement program and connect with leading companies across industries.
                                        Our dedicated training team will help you prepare for interviews and assessments.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        onClick={() => navigate("/sign-in")}
                                        sx={{
                                            mt: 2,
                                            py: 1.2,
                                            px: 3,
                                            borderRadius: 2,
                                            fontWeight: 600
                                        }}
                                    >
                                        Register for Placements
                                    </Button>
                                </Grid>
                                <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <Box
                                        component="img"
                                        src="/logo-pc.png"
                                        alt="Career Growth"
                                        sx={{
                                            width: '100%',
                                            borderRadius: 3,
                                            background: "white",
                                            boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Decorative elements */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -40,
                                    right: -40,
                                    width: 120,
                                    height: 120,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(25, 118, 210, 0.1)',
                                    zIndex: 0
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: -30,
                                    left: -30,
                                    width: 100,
                                    height: 100,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(25, 118, 210, 0.05)',
                                    zIndex: 0
                                }}
                            />
                        </Paper>
                    </Box>
                </Container>
            </Box>

            {/* Director's Message */}
            <Box
                id="director-message"
                className="section"
                sx={{
                    py: 10,
                    bgcolor: '#f8f9fa',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={5} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <Box sx={{ position: 'relative' }}>
                                <Avatar
                                    alt="Prof. H.S. Rawat"
                                    src="https://www.msit.in/media/2021/07/19/hs.png"
                                    sx={{
                                        width: { xs: 200, md: 250 },
                                        height: { xs: 200, md: 250 },
                                        mx: 'auto',
                                        border: '5px solid white',
                                        boxShadow: '0 10px 20px rgba(0,0,0,0.15)',
                                        transform: 'scaleX(-1)'
                                    }}
                                />
                                <Box sx={{ mt: 3, textAlign: 'center' }}>
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        Prof. H.S. Rawat
                                    </Typography>
                                    <Typography variant="subtitle1" color="text.secondary">
                                        Director - Training & Placement, SMES
                                    </Typography>
                                    <a href="mailto:msit.tpo@gmail.com" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        <Email fontSize="small" sx={{ mr: 1, fontSize: '1rem', verticalAlign: 'middle' }} />
                                        msit.tpo@gmail.com
                                    </a>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                                DIRECTOR'S MESSAGE
                            </Typography>
                            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
                                From the Director's Desk
                            </Typography>

                            <Box sx={{ position: 'relative' }}>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        position: 'absolute',
                                        top: -30,
                                        left: -15,
                                        fontSize: '8rem',
                                        color: 'rgba(0, 0, 0, 0.03)',
                                        fontFamily: 'serif'
                                    }}
                                >
                                    "
                                </Typography>

                                <Typography variant="body1" paragraph sx={{ position: 'relative', pl: 3 }}>
                                    Prof. H. S. Rawat is presently working as Director- T & P in Maharaja Surajmal Group of Institutions (MSGI),
                                    C-4, Janak Puri, New Delhi. He has done B.Sc. (PCM), B.E. (Comp. Sc. Engg.), PGDIM, MBA and Ph.D.
                                    He has 28(+) years professional experience including 11(+) years with industry and 17(+) years with educational institutes.
                                </Typography>
                            </Box>

                            <List sx={{ mt: 2 }}>
                                <ListItem sx={{ px: 0 }}>
                                    <ListItemIcon>
                                        <Cake color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Date of Birth: 05-02-1966"
                                    />
                                </ListItem>
                                <ListItem sx={{ px: 0 }}>
                                    <ListItemIcon>
                                        <School color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Teaching Experience: 17+ years"
                                    />
                                </ListItem>
                                <ListItem sx={{ px: 0 }}>
                                    <ListItemIcon>
                                        <WorkHistory color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Industry Experience: 11+ years"
                                    />
                                </ListItem>
                            </List>

                            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                                Prof. H.S. Rawat, Currently taking care of Training & Placements for Maharaja Surajmal Group of Institutions (MSGI).
                                Always working towards excellence in placements for each of the institutes, setting up new paradigms and benchmarks.
                                Have been instrumental for best of tie-ups & relationships with big MNC's & some of the top notch companies.
                            </Typography>

                            <Box sx={{ mt: 3 }}>
                                <Chip
                                    icon={<Star />}
                                    label="National CSR Leadership Award 2018"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ m: 0.5 }}
                                />
                                <Chip
                                    icon={<Star />}
                                    label="Campus Preparedness Award 2015"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ m: 0.5 }}
                                />
                                <Chip
                                    icon={<Star />}
                                    label="CMAI National U.P. Education Award 2013"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ m: 0.5 }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Placement Committee */}
            <Box id="committee" className="section" sx={{ py: 10 }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                            OUR TEAM
                        </Typography>
                        <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
                            Placement Committee
                        </Typography>
                        <Typography variant="body1" sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
                            Placement Committee MSIT has been formed with a strong emphasis and aim to achieve the target of 100% placements
                            of all the interested and eligible students. The Committee facilitates the students and coordinates during campus drives.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 4,
                                borderRadius: 3,
                                bgcolor: 'rgba(25, 118, 210, 0.04)',
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: 'center',
                                gap: 3
                            }}
                        >
                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Avatar
                                    alt="Dr. Meena Rao"
                                    src="https://www.msit.in/media/faculty/meena-rao.jpg"
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        mx: 'auto',
                                        border: '4px solid white',
                                        boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
                                    }}
                                />
                                <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
                                    Dr. Meena Rao
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary">
                                    Convener
                                </Typography>
                            </Box>

                            <Divider orientation={isMobile ? "horizontal" : "vertical"} flexItem sx={{ my: 2 }} />

                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Avatar
                                    alt="Dr. Kavita Sheoran"
                                    src="https://www.msit.in/media/faculty/ms-kavita-sheoran.jpg"
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        mx: 'auto',
                                        border: '4px solid white',
                                        boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
                                    }}
                                />
                                <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
                                    Dr. Kavita Sheoran
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary">
                                    Co-convener
                                </Typography>
                            </Box>

                            <Divider orientation={isMobile ? "horizontal" : "vertical"} flexItem sx={{ my: 2 }} />

                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Avatar
                                    alt="Mr. Parveen Kumar"
                                    src="https://www.msit.in/media/faculty/parveen-kumar.jpg"
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        mx: 'auto',
                                        border: '4px solid white',
                                        boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
                                    }}
                                />
                                <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
                                    Mr. Parveen Kumar
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary">
                                    Co-convener
                                </Typography>
                            </Box>
                        </Paper>
                    </Box>

                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 6, mb: 3, textAlign: 'center' }}>
                        Faculty Coordinators
                    </Typography>

                    <Grid container spacing={3}>
                        {facultyCoordinators.map((faculty, index) => (
                            <Grid item xs={6} sm={4} md={3} key={index}>
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 2,
                                        textAlign: 'center',
                                        height: '100%',
                                        transition: 'transform 0.3s',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                                        },
                                        borderRadius: 2
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {faculty.name}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={faculty.class}
                                        color="primary"
                                        sx={{ mt: 1 }}
                                    />
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Footer */}
            <Box sx={{ bgcolor: '#1976d2', color: 'white', py: 6 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ mb: 3 }}>
                                <Box
                                    component="img"
                                    src="https://msit.in/static/img/msit.png"
                                    alt="MSIT Logo"
                                    sx={{ height: 70 }}
                                />
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Maharaja Surajmal Institute of Technology<br />
                                C-4, Janak Puri, New Delhi-110058
                            </Typography>
                            <a href='mailto:msit.tpo@gmail.com' variant="body2">
                                <Email fontSize="small" sx={{ mr: 1, fontSize: '1rem', verticalAlign: 'middle' }} />
                                msit.tpo@gmail.com
                            </a>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Quick Links
                            </Typography>
                            <List disablePadding>
                                {[['Home', '/'], ['About Us', '/meet-the-team'], ['Placement Records', '#placement-records'], ['Student Login', '/sign-in']].map(([item, path], index) => (
                                    <ListItem key={index} sx={{ p: 0, mb: 1 }}>
                                        <KeyboardArrowRight fontSize="small" sx={{ mr: 1, color: '#90caf9' }} />
                                        <Link href={path} color="inherit" underline="hover" sx={{ opacity: 0.9 }}>
                                            {item}
                                        </Link>
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Connect With Us
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 3, mt: 2 }}>
                                {[['facebook', "https://www.facebook.com/msitnewdelhi/"], ['twitter', "https://x.com/msitnewdelhi"], ['linkedin', "https://in.linkedin.com/school/maharaja-surajmal-institute-of-technology-msitnewdelhi/"], ['instagram', "https://www.instagram.com/msitnewdelhi/?hl=en"], ['lionair', "https://www.linkedin.com/in/mohit8181"]].map(([social, link]) => (
                                    <IconButton onClick={() => window.open(link, "_blank")} key={social} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}>
                                        <Box
                                            component="img"
                                            src={`https://cdn.jsdelivr.net/npm/simple-icons@v5/icons/${social}.svg`}
                                            alt={social}
                                            sx={{ height: 20, filter: 'invert(1)' }}
                                        />
                                    </IconButton>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 4, bgcolor: 'rgba(255,255,255,0.1)' }} />

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'center', sm: 'flex-start' } }}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            © {new Date().getFullYear()} MSIT Placement Cell. All rights reserved.
                        </Typography>
                        <Box sx={{ mt: { xs: 2, sm: 0 } }}>
                            <Link href="#" color="inherit" underline="hover" sx={{ mx: 1, opacity: 0.8 }}>
                                Privacy Policy
                            </Link>
                            <Link href="#" color="inherit" underline="hover" sx={{ mx: 1, opacity: 0.8 }}>
                                Terms of Service
                            </Link>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Scroll to top button */}
            <ScrollTop>
                <IconButton
                    aria-label="scroll back to top"
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                            bgcolor: 'primary.dark',
                        },
                    }}
                >
                    <KeyboardArrowDown sx={{ transform: 'rotate(180deg)' }} />
                </IconButton>
            </ScrollTop>
        </>
    );
};

export default Landing;